import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, LoginForm, RegisterForm } from '../types';
import { apiService } from '../services/api';

interface AuthStore extends AuthState {
  login: (credentials: LoginForm) => Promise<boolean | 'user_not_found'>;
  register: (userData: RegisterForm) => Promise<boolean>;
  logout: () => void;
  updateBalance: (amount: number) => void;
  checkAuth: () => Promise<boolean>;
  error: string | null;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setError: (error: string | null) => set({ error }),

      login: async (credentials: LoginForm): Promise<boolean | 'user_not_found'> => {
        set({ isLoading: true, error: null });
        
        try {
          const { user } = await apiService.login(credentials);
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          return true;
        } catch (error: any) {
          // Check if it's a 401 error indicating user doesn't exist or invalid credentials
          const isUnauthorized = error.response?.status === 401;
          
          if (isUnauthorized) {
            set({
              error: null, // Don't show error here, we'll redirect
              isLoading: false
            });
            return 'user_not_found';
          }
          
          set({
            error: error.message || 'Login failed. Please try again.',
            isLoading: false
          });
          return false;
        }
      },

      register: async (userData: RegisterForm): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          // Check if passwords match (frontend validation)
          if (userData.password !== userData.confirmPassword) {
            set({
              error: 'Passwords do not match',
              isLoading: false
            });
            return false;
          }
          
          const { user } = await apiService.register(userData);
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          return true;
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed. Please try again.',
            isLoading: false
          });
          return false;
        }
      },

      logout: () => {
        apiService.clearAuth();
        set({
          user: null,
          isAuthenticated: false,
          error: null
        });
      },

      updateBalance: (amount: number) => {
        const { user } = get();
        if (user) {
          const updatedUser = {
            ...user,
            total_spent: (user.total_spent || 0) + amount
          };
          set({ user: updatedUser });
        }
      },

      checkAuth: async (): Promise<boolean> => {
        // First check if we have a token and if it's not expired
        if (!apiService.isAuthenticated()) {
          set({
            user: null,
            isAuthenticated: false,
            error: null
          });
          return false;
        }

        // If we have a valid token, verify it with the server
        try {
          const user = await apiService.verifyToken();
          set({
            user,
            isAuthenticated: true,
            error: null
          });
          return true;
        } catch (error) {
          // Token is invalid or expired
          apiService.clearAuth();
          set({
            user: null,
            isAuthenticated: false,
            error: null
          });
          return false;
        }
      }
    }),
    {
      name: 'neubird-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
      // Rehydrate with validation
      onRehydrateStorage: () => (state) => {
        if (state?.isAuthenticated && !apiService.isAuthenticated()) {
          // If persisted state says authenticated but token is invalid, clear it
          state.isAuthenticated = false;
          state.user = null;
        }
      }
    }
  )
);
